/**
 * Pure helpers for the media library: client-side upload validation, size
 * formatting, processing-state derivation and the tag/editorial filtering the
 * library page applies to a page of search results.
 *
 * Kept free of React and axios so mediaHelpers.test.js can exercise them.
 */

/* Mirrors ALLOWED_MEDIA_TYPES in backend/app.py. Rejecting here is a courtesy;
   the backend re-validates every upload. */
export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Human-readable file size. formatBytes(1536) === '1.5 KB' */
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

/**
 * Validate one picked file.
 * Returns { ok: true } or { ok: false, error: 'why' } — never throws, so a
 * drag-and-drop of a folder or a zero-byte file is reported, not crashed on.
 */
export function validateFile(file) {
  if (!file) return { ok: false, error: 'No file selected' };
  const type = String(file.type || '').split(';')[0].trim().toLowerCase();
  if (!ALLOWED_MIME_TYPES.includes(type)) {
    return { ok: false, error: `${file.name || 'File'} is not a JPEG, PNG, WebP or GIF` };
  }
  if (typeof file.size === 'number' && file.size <= 0) {
    return { ok: false, error: `${file.name || 'File'} is empty` };
  }
  if (typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES) {
    return { ok: false, error: `${file.name || 'File'} is ${formatBytes(file.size)}; the limit is 20 MB` };
  }
  return { ok: true };
}

/** Split the validated files from the rejected ones, preserving order. */
export function partitionFiles(files) {
  const accepted = [];
  const rejected = [];
  for (const file of Array.from(files || [])) {
    const result = validateFile(file);
    if (result.ok) accepted.push(file);
    else rejected.push({ file, error: result.error });
  }
  return { accepted, rejected };
}

/**
 * True while the resize webhook has not produced a thumbnail yet.
 * The doc's `processing` flag is never cleared by the webhook, so the presence
 * of thumb_path is the authority and the flag only covers the window before the
 * webhook has written anything at all.
 */
export function isProcessing(media) {
  if (!media) return false;
  if (media.thumb_path || hasThumbUrl(media)) return false;
  return !!(media.processing || media.raw_path);
}

/* GET /images puts the sentinels 'missing thumb_path' and 'error' in thumb_url
   instead of omitting it, so only a real URL counts as a thumbnail. */
function hasThumbUrl(media) {
  return /^https?:\/\//.test(String(media.thumb_url || ''));
}

/** 'processing' | 'ready' | 'unknown' — drives the card/detail badge. */
export function mediaState(media) {
  if (!media) return 'unknown';
  if (isProcessing(media)) return 'processing';
  if (media.thumb_path || hasThumbUrl(media)) return 'ready';
  return 'unknown';
}

/**
 * Tag chips from the server's tag facet ({ tag: count } over the whole result
 * set): selected tags first, then most-used then A–Z, cut to `limit`.
 * Returns { chips: [{ tag, count }], hidden } where hidden is how many were cut.
 */
export function tagChips(tagCounts, activeTags = [], limit = Infinity) {
  const counts = { ...(tagCounts || {}) };
  for (const tag of activeTags || []) {
    if (!(tag in counts)) counts[tag] = 0;
  }
  const sorted = Object.entries(counts)
    .map(([tag, count]) => ({ tag, count }))
    .filter(({ tag }) => String(tag).trim())
    .sort((a, b) => {
      const activeA = activeTags.includes(a.tag);
      const activeB = activeTags.includes(b.tag);
      if (activeA !== activeB) return activeA ? -1 : 1;
      return (b.count - a.count) || a.tag.localeCompare(b.tag);
    });
  const keep = Math.max(limit, (activeTags || []).length);
  return { chips: sorted.slice(0, keep), hidden: Math.max(0, sorted.length - keep) };
}

/**
 * Page numbers for a numbered pager: first, last and a window around the
 * current page, with '…' where pages are skipped. pageWindow(6, 20) →
 * [1, '…', 4, 5, 6, 7, 8, '…', 20].
 */
export function pageWindow(page, totalPages, radius = 2) {
  if (totalPages <= 1) return [1];
  const pages = [];
  let from = Math.max(2, page - radius);
  let to = Math.min(totalPages - 1, page + radius);
  // An ellipsis standing in for a single page is no shorter than the page.
  if (from === 3) from = 2;
  if (to === totalPages - 2) to = totalPages - 1;
  pages.push(1);
  if (from > 2) pages.push('…');
  for (let p = from; p <= to; p += 1) pages.push(p);
  if (to < totalPages - 1) pages.push('…');
  pages.push(totalPages);
  return pages;
}

/** Normalise a typed tag list: trimmed, de-duplicated, order preserved. */
export function normalizeTags(tags) {
  const seen = [];
  for (const raw of tags || []) {
    const tag = String(raw || '').trim();
    if (tag && !seen.includes(tag)) seen.push(tag);
  }
  return seen;
}

/** A media item's best display name. */
export function mediaLabel(media) {
  if (!media) return '';
  return media.name || media.original_filename || media.id || media._id || '';
}
